import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import { apiFetchAuthed } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useChargeStore from '../stores/useChargeStore';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import { Field } from '../components/ui/Field';
import SearchField from '../components/ui/SearchField';
import Switch from '../components/ui/Switch';
import Checkbox from '../components/ui/Checkbox';
import Reveal from '../components/ui/Reveal';
import { useExiting } from '../components/ui/motion';
import type { ChargerView, LocationItem, FleetResponse } from './types';

export default function AssignmentsEditor({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const {
    locations, prefs, createLocation, deleteLocation,
    getMembers, setMembers, setPinned, fetchNearby,
  } = useChargeStore();

  const [candidates, setCandidates] = useState<ChargerView[]>([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#ffcc33');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The members the open group was loaded or last saved with, to tell an unsaved pick from a clean one.
  const savedMembers = useRef<Set<string>>(new Set());
  // Which group `savedMembers` belongs to. Null while a fetch is out, so a group still loading reads clean.
  const loadedFor = useRef<number | null>(null);
  // The group last asked for: a slower fetch for a group opened earlier must not land on it.
  const openingFor = useRef<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [nearbyMsg, setNearbyMsg] = useState<string | null>(null);
  const [showOffline, setShowOffline] = useState(false);
  const [search, setSearch] = useState('');
  // After adding a charger, offer its online neighbors on the same network.
  const [sameNetGroup, setSameNetGroup] = useState<{ triggerLabel: string; ids: string[] } | null>(null);

  const custom = locations;
  const shownGroups = useExiting(custom, (l) => String(l.id));

  // Candidate pool for a team group = internal-pool chargers only (groups never
  // hold customer gear; goals are internal-gated anyway). Fetched via find-mode
  // (search=""); the filters below narrow the view.
  useEffect(() => {
    apiFetchAuthed('/charge/fleet?search=', { silent: true })
      .then((r: FleetResponse) => setCandidates((r.chargers || []).filter((c) => c.is_internal_pool)))
      .catch(() => setCandidates([]));
  }, []);

  // Default hide offline; always keep current members visible so they can be
  // unchecked; plus a free-text search.
  const visibleCandidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter((c) => {
      if (!c.asset_id) return false;
      if (selected.has(c.asset_id)) return true;
      if (!showOffline && !c.online) return false;
      if (q) {
        const hay = `${c.display_code || ''} ${c.vpn_ip} ${c.geo_label || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [candidates, showOffline, search, selected]);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const row = await createLocation(newName.trim(), newColor);
      toast.success(`Created “${row.name}”`);
      setNewName('');
      openMembers(row.id as number);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create location');
    } finally { setBusy(false); }
  };

  const openMembers = async (id: number) => {
    loadedFor.current = null;
    openingFor.current = id;
    setEditingId(id);
    setNearbyMsg(null);
    setSameNetGroup(null);
    // Reset the filter view so each location's editor opens clean.
    setSearch('');
    setShowOffline(false);
    try {
      const members = await getMembers(id);
      if (openingFor.current !== id) return;
      savedMembers.current = new Set(members.map((m) => m.asset_id));
      loadedFor.current = id;
      setSelected(new Set(savedMembers.current));
    } catch {
      if (openingFor.current !== id) return;
      savedMembers.current = new Set(); loadedFor.current = id; setSelected(new Set());
    }
  };

  const toggle = (assetId: string) => {
    const wasSelected = selected.has(assetId);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
    if (wasSelected) { setSameNetGroup(null); return; }
    // Just added — suggest its ONLINE same-network mates not already in the group.
    const c = candidates.find((x) => x.asset_id === assetId);
    if (c && c.online && c.network_id) {
      const mates = candidates.filter((m) =>
        m.asset_id && m.online && m.network_id === c.network_id &&
        m.asset_id !== assetId && !selected.has(m.asset_id));
      setSameNetGroup(mates.length
        ? { triggerLabel: c.display_code || c.vpn_ip, ids: mates.map((m) => m.asset_id as string) }
        : null);
    } else {
      setSameNetGroup(null);
    }
  };

  const addMate = (assetId: string) => {
    setSelected((prev) => new Set(prev).add(assetId));
    setSameNetGroup((s) => (s ? { ...s, ids: s.ids.filter((id) => id !== assetId) } : s));
  };

  const saveMembers = async () => {
    if (editingId == null) return;
    setBusy(true);
    try {
      await setMembers(editingId, [...selected]);
      toast.success('Chargers updated');
      savedMembers.current = new Set(selected);
      setEditingId(null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save chargers');
    } finally { setBusy(false); }
  };

  const findNearby = () => {
    if (!navigator.geolocation) { toast.error('Location not available on this device'); return; }
    setNearbyMsg('Locating…');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const clusters = await fetchNearby(pos.coords.latitude, pos.coords.longitude);
          if (!clusters.length) { setNearbyMsg('No chargers found nearby.'); return; }
          const c = clusters[0];
          const ids = c.chargers.map((x) => x.asset_id).filter(Boolean) as string[];
          setSelected((prev) => new Set([...prev, ...ids]));
          setNearbyMsg(`Added ${ids.length} charger(s) near ${c.geo_label || 'you'}${c.distance_km != null ? ` (~${c.distance_km} km)` : ''}.`);
        } catch (e: any) {
          setNearbyMsg(e?.message || 'Nearby lookup failed.');
        }
      },
      () => setNearbyMsg('Could not get your location (permission denied?).'),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const remove = async (loc: LocationItem) => {
    if (!window.confirm(`Delete “${loc.name}”? This removes it for everyone.`)) return;
    try {
      await deleteLocation(loc.id as number);
      toast.success('Location deleted');
      if (editingId === loc.id) setEditingId(null);
    } catch (e: any) { toast.error(e?.message || 'Failed to delete'); }
  };

  const togglePin = async (loc: LocationItem) => {
    const pinned = new Set(prefs.pinned);
    pinned.has(loc.id as number) ? pinned.delete(loc.id as number) : pinned.add(loc.id as number);
    try { await setPinned([...pinned]); }
    catch (e: any) { toast.error(e?.message || 'Failed to update pins'); }
  };

  const dirty = newName !== '' || (editingId != null && loadedFor.current === editingId
    && (selected.size !== savedMembers.current.size || [...selected].some((id) => !savedMembers.current.has(id))));

  return (
    <Dialog open onClose={onClose} dirty={dirty} title="Manage groups" closeHint="Close group management" size="lg">
        {/* New location */}
        <Panel on="panel" padding="sm" title="New group" level={3} className="mb-3">
          <div className="flex items-center gap-2">
            <Field
              value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Texas Tour" aria-label="New group name"
              className="flex-1"
            />
            <input
              type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
              className="w-9 h-9 m-0 bg-transparent border border-outline-variant rounded cursor-pointer" aria-label="Color"
            />
            <button type="button" onClick={create} disabled={busy || !newName.trim()}
              className={buttonClass('filled', 'md')}>Add</button>
          </div>
        </Panel>

        {/* Existing locations */}
        <div className="space-y-2">
          <Reveal show={custom.length === 0}>
            <EmptyState variant="line">No groups yet. Create one above to organize chargers for the team.</EmptyState>
          </Reveal>
          {shownGroups.map(({ item: loc, leaving }) => (
            <Reveal key={loc.id} show={!leaving}>
            <Panel on="panel" padding="none" label={loc.name}>
              <div className="flex items-center gap-2 p-3">
                {loc.color && <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: loc.color }} />}
                <span className="font-medium text-white flex-1 truncate">{loc.name}</span>
                <span className="text-xs text-on-surface-variant">{loc.member_count}</span>
                <button type="button" onClick={() => togglePin(loc)}
                  className={`text-xs px-2 py-1 rounded border transition ${
                    loc.pinned ? 'border-primary text-primary' : 'border-outline-variant text-on-surface-variant hover:text-on-surface'
                  }`}>{loc.pinned ? 'Pinned' : 'Pin'}</button>
                <button type="button" data-handoff="" onClick={() => (editingId === loc.id ? setEditingId(null) : openMembers(loc.id as number))}
                  className={buttonClass('quiet', 'sm')}>
                  {editingId === loc.id ? 'Close' : 'Chargers'}</button>
                <button type="button" onClick={() => remove(loc)}
                  className={buttonClass('danger-quiet', 'sm')}>Delete</button>
              </div>

              <Reveal show={editingId === loc.id}>
                <div className="border-t border-gray-700 p-3">
                  <SearchField
                    value={search} onChange={setSearch}
                    placeholder="Search by code or location…"
                    label="Search chargers by code or location"
                    className="mb-2"
                  />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-2 text-xs text-on-surface-variant">
                    <Switch checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)} label="Show offline" />
                    <button type="button" onClick={findNearby} className={buttonClass('quiet', 'sm')}>Find near me</button>
                    <span className="ml-auto">{selected.size} selected</span>
                  </div>
                  <Reveal show={!!nearbyMsg}>
                    <div className="text-xs text-on-surface-variant mb-2">{nearbyMsg}</div>
                  </Reveal>
                  <Reveal show={!!sameNetGroup && sameNetGroup.ids.length > 0}>
                  {sameNetGroup && sameNetGroup.ids.length > 0 && (
                    <div className="mb-2 rounded border border-primary/40 bg-primary/5 p-2">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs text-on-surface">
                          Add more on the same network as <span className="font-mono text-white">{sameNetGroup.triggerLabel}</span>?
                        </span>
                        <Tooltip label="Dismiss this suggestion"><IconButton size="sm" label="Dismiss this suggestion" onClick={() => setSameNetGroup(null)}><IconClose /></IconButton></Tooltip>
                      </div>
                      <div className="space-y-1">
                        {sameNetGroup.ids.map((id) => {
                          const m = candidates.find((x) => x.asset_id === id);
                          if (!m) return null;
                          return (
                            <div key={id} className="flex items-center gap-2 text-sm">
                              <span className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
                              <span className="text-white flex-shrink-0">{m.display_code || m.vpn_ip}</span>
                              {m.geo_label && <span className="text-xs text-on-surface-variant truncate min-w-0">{m.geo_label}</span>}
                              <button type="button" onClick={() => addMate(id)}
                                className={`${buttonClass('quiet', 'sm')} ml-auto flex-shrink-0`}>Add</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  </Reveal>
                  <div className="max-h-52 overflow-y-auto space-y-1 pr-1">
                    {visibleCandidates.map((c) => (
                      <Checkbox key={c.asset_id} fill className="py-1"
                        checked={selected.has(c.asset_id as string)}
                        onChange={() => toggle(c.asset_id as string)}
                        label={
                          <span className="flex items-center gap-2">
                            <span role="img" aria-label={c.online ? 'Online' : 'Offline'}
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${c.online ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                            <span className="text-white flex-shrink-0">{c.display_code || c.vpn_ip}</span>
                            {c.geo_label && <span className="text-xs text-on-surface-variant truncate min-w-0">{c.geo_label}</span>}
                          </span>
                        } />
                    ))}
                    {visibleCandidates.length === 0 && (
                      <EmptyState variant="line" dense>No chargers match. Try “Show offline”.</EmptyState>
                    )}
                  </div>
                  <button type="button" onClick={saveMembers} disabled={busy}
                    className={`${buttonClass('filled', 'md')} w-full mt-3`}>
                    {busy ? 'Saving…' : 'Save chargers'}</button>
                </div>
              </Reveal>
            </Panel>
            </Reveal>
          ))}
        </div>
    </Dialog>
  );
}
